import React from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { TFunction } from 'i18next';
import CustomModal from '../../../components/atoms/CustomModal';
import Button from '../../../components/atoms/Button';
import FormField from '../../../components/FormField/FormField';
import { PlanBenefitItem } from '../../../types/marketplacePlans';
import { arabicRegex, englishRegex } from '../../../constants/regex';

export const benefitFormSchema = (t: TFunction) =>
  z.object({
    benefitTextEn: z
      .string({ required_error: t('requiredField') })
      .trim()
      .min(1, t('requiredField'))
      .regex(englishRegex, t('validation.minCharEnglish')),
    benefitTextAr: z
      .string({ required_error: t('requiredField') })
      .trim()
      .min(1, t('requiredField'))
      .regex(arabicRegex, t('validation.minCharArabic')),
  });

export type BenefitFormValues = z.infer<ReturnType<typeof benefitFormSchema>>;

interface BenefitModalProps {
  isOpen: boolean;
  editingBenefit: PlanBenefitItem | null;
  onClose: () => void;
  onSave: (values: BenefitFormValues) => void;
}

export default function BenefitModal({
  isOpen,
  editingBenefit,
  onClose,
  onSave,
}: BenefitModalProps) {
  const { t } = useTranslation();

  const methods = useForm<BenefitFormValues>({
    resolver: zodResolver(benefitFormSchema(t)),
    mode: 'onChange',
    defaultValues: {
      benefitTextEn: '',
      benefitTextAr: '',
    },
  });

  React.useEffect(() => {
    if (editingBenefit) {
      methods.reset({
        benefitTextEn: editingBenefit.textEn,
        benefitTextAr: editingBenefit.textAr,
      });
    } else {
      methods.reset({
        benefitTextEn: '',
        benefitTextAr: '',
      });
    }
  }, [editingBenefit, isOpen, methods]);

  const handleClose = () => {
    methods.reset();
    onClose();
  };

  const handleSubmit = (values: BenefitFormValues) => {
    onSave(values);
    handleClose();
  };

  return (
    <CustomModal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('home.add-plan-information-title')}
      subTitle={t('home.add-plan-information-desc')}
      width={560}
    >
      <FormProvider {...methods}>
        <form
          onSubmit={methods.handleSubmit(handleSubmit)}
          className="flex flex-col gap-5 mt-4"
          data-testid="benefit-modal-form"
        >
          <div data-testid="benefit-en-container">
            <FormField
              type="text"
              id="benefitTextEn"
              name="benefitTextEn"
              label={t('home.plan-information-en')}
              placeholder={t('home.set')}
              className="!rounded-xl"
              required
              data-testid="benefit-en-input"
            />
          </div>
          <div data-testid="benefit-ar-container">
            <FormField
              type="text"
              id="benefitTextAr"
              name="benefitTextAr"
              label={t('home.plan-information-ar')}
              placeholder={t('home.set')}
              className="!rounded-xl"
              required
              data-testid="benefit-ar-input"
            />
          </div>
          <div
            className="flex justify-end items-center gap-3 pt-4"
            data-testid="benefit-modal-actions"
          >
            <Button
              variant="outline"
              type="button"
              onClick={handleClose}
              className="rounded-xl px-6 py-2"
              data-testid="benefit-modal-cancel-btn"
            >
              {t('cancel')}
            </Button>
            <Button
              variant="dark"
              type="submit"
              className="rounded-xl px-6 py-2"
              data-testid="benefit-modal-save-btn"
            >
              {t('save')}
            </Button>
          </div>
        </form>
      </FormProvider>
    </CustomModal>
  );
}
